import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Action } from '@interstate/components/Action';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import { Box } from '@interstate/components/Box';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { Popover } from '@interstate/components/Popover';
import { Typography } from '@interstate/components/Typography';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { getBuyer } from '../../../store/selectors';
import type { CustomerCardStoreState } from '../../../types/CustomerCardTypes';
import { extractAddressFields, getFormattedAddress, areAddressesEqual } from './address.utils';
import { AddressCopyContainer, CheckBoxRow, PopoverRow } from './address.styles';
import type { Address } from './address.utils';

const DISCLAIMER = '(This will replace/override any existing co-buyer addresses)';

type BuyerAddressCopyProps = {
    setIsPrimaryEditing: (editing: boolean) => void;
    setIsBillingEditing: (editing: boolean) => void;
};

/**
 * Checkbox + popover shown at the top of the co-buyer address section.
 * When checked, copies the buyer's current stored addresses into the
 * co-buyer form and switches both address cards to view mode.
 */
const BuyerAddressCopy = ({ setIsPrimaryEditing, setIsBillingEditing }: BuyerAddressCopyProps) => {
    const { formValues, handleSetValues, isLocked } = useCustomerCardContext();
    const buyer = useSelector((state: CustomerCardStoreState) => getBuyer(state));

    const buyerPrimary = extractAddressFields((buyer ?? {}) as Address);

    const buyerBilling: Address | undefined = buyer?.billingAddress
        ? extractAddressFields(buyer.billingAddress as Address)
        : undefined;

    const currentPrimary = extractAddressFields(formValues as Address);

    const currentBilling: Address | undefined = formValues.billingAddress
        ? extractAddressFields(formValues.billingAddress as Address)
        : undefined;

    const [isChecked, setIsChecked] = useState(
        // Checkbox starts checked when co-buyer addresses already match buyer's.
        () =>
            areAddressesEqual(currentPrimary, buyerPrimary) &&
            (buyerBilling ? !!currentBilling && areAddressesEqual(currentBilling, buyerBilling) : !currentBilling)
    );

    const handleChange = (e: InterstateOnChangeEvent<CheckBoxEventValue>) => {
        const { checked } = e.target.value;
        setIsChecked(checked);
        if (checked) {
            const update: Record<string, unknown> = { ...buyerPrimary };
            if (buyerBilling) {
                update.billingAddress = buyerBilling;
            }
            handleSetValues(update);
            setIsPrimaryEditing(false);
            setIsBillingEditing(false);
        }
    };

    const addressRow = (sectionTitle: string, lines: string[]) => (
        <Typography tag="span" variant="h6" sx={{ fontWeight: 'semi-bold' }}>
            {`${sectionTitle}: `}
            <Typography tag="span" variant="body-sm" sx={{ fontWeight: 'normal' }}>
                {lines.join(' ')}
            </Typography>
        </Typography>
    );

    const popoverContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <PopoverRow>{addressRow('Primary', getFormattedAddress(buyerPrimary))}</PopoverRow>
            {buyerBilling && <PopoverRow>{addressRow('Billing', getFormattedAddress(buyerBilling))}</PopoverRow>}
        </Box>
    );

    return (
        <AddressCopyContainer data-testid="buyer-address-copy-container">
            <CheckBoxRow>
                <CheckBox
                    id="address-copy-checkbox"
                    checked={isChecked}
                    value=""
                    label="Same as"
                    onChange={handleChange}
                    data-testid="address-copy-checkbox"
                    disabled={isLocked}
                />
                <Popover
                    padding
                    header="Buyer's Addresses"
                    data-testid="buyer-addresses-popover"
                    position="top-start"
                    trigger="outsideClick"
                    popoverContent={popoverContent}
                    variant="withPointer"
                >
                    <Action data-testid="view-buyer-addresses-button" size="sm" disabled={isLocked}>
                        Buyer's
                    </Action>
                </Popover>
                <Typography tag="span" variant="body-md">
                    Addresses
                </Typography>
            </CheckBoxRow>
            <Typography tag="span" variant="body-sm" sx={{ fontStyle: 'italic' }}>
                {DISCLAIMER}
            </Typography>
        </AddressCopyContainer>
    );
};

export default BuyerAddressCopy;
